/**
 * Login Page Component
 * Handles user authentication for the HEAT Client Portal
 * 
 * Supports two user types:
 * 1. Client Portal Users - redirects directly to dashboard
 * 2. Admin Users (Master Key) - redirects to client selection page
 */
import React, { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { auth } from '../auth';
import './Login.css';
import ConstellationBackground from '../components/ConstellationBackground';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Call login API
      const response = await api.login(email, password);
      
      // Store authentication data (handles both user types)
      auth.setAuth(response);
      
      // Check if this is an admin user (master key access)
      if (response.user.isAdminPortalUser) {
        // Admin user - redirect to client selection page
        navigate('/select-client');
      } else {
        // Regular client user - redirect directly to dashboard
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <ConstellationBackground />
      <div className="login-logo-header">
        <img 
          src="/heat-logo.png" 
          alt="HEAT Logo" 
          className="login-logo"
        />
        <p className="login-tagline">Strategic Intelligence Platform</p>
      </div>
      
      <div className="login-box">
        <div className="login-header">
          <h1>HEAT Client Portal</h1>
          <p>Sign in to access your dashboard</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="login-footer">
          <p>Need help? Contact your administrator</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
